import React, { useState, useEffect, Component } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../services/apiService";
import { useCarContext } from "../CarContext";
import { Loading } from "../components/Loading";
import locCard from "../assets/locCard.svg";
import rc_yes from "../assets/rc-yes.svg";
import rc_no from "../assets/rc-no.svg";
import "./UploadPictures.css";
import "../DealerHome.css";
import marutiImg from "../assets/maruti.png";
import { Button, ImageWithFallback } from "../components/Utilities";
import share from "../assets/share.svg";
import imgTop from "../assets/img_top.svg";
import imgBottom from "../assets/img_bottom.svg";
import Delete from "../assets/Delete.svg";
import {
  UploadModal,
  UploadPicturesModel,
} from "../components/ModelOverlay/UploadPicturesModel";
import { formatOwnership } from "../DealerHome";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h3>Something went wrong</h3>
          <p>{this.state.error?.message || "An unexpected error occurred."}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Utility function to format time left
const formatTimeLeft = (taEndDateStr) => {
  if (!taEndDateStr || typeof taEndDateStr !== "string") {
    console.warn("Invalid ta_end_date:", taEndDateStr);
    return "N/A";
  }
  const endDate = new Date(taEndDateStr);
  if (isNaN(endDate.getTime())) {
    console.warn("Invalid date format for ta_end_date:", taEndDateStr);
    return "N/A";
  }
  const now = new Date();
  const diffMs = endDate - now;

  if (diffMs <= 0) {
    return "Expired";
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(diffSeconds / (3600 * 24));
  const hours = Math.floor((diffSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;

  if (days > 0) {
    return `${days}D:${hours.toString().padStart(2, "0")}H`;
  } else if (hours > 0) {
    return `${hours}H:${minutes.toString().padStart(2, "0")}M`;
  } else if (minutes > 0) {
    return `${minutes}M:${seconds.toString().padStart(2, "0")}S`;
  } else {
    return `${seconds}S`;
  }
};

export default function UploadPictures() {
  const navigate = useNavigate();
  const {
    uploadPicturesCount,
    setUploadPicturesCount,
    placeBidsCount,
    setPlaceBidsCount,
  } = useCarContext();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedCarId, setSelectedCarId] = useState(null);
  const [uploadedPictures, setUploadedPictures] = useState({});
  const [cars, setCars] = useState([]);
  const [cachedCars, setCachedCars] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false); // NEW: State to track remove/move actions

  // Handle Move to Place Bid (Updated)
  const handleMoveToPlaceBid = async (carId) => {
    setActionLoading(true); // Start loading
    try {
      console.log("Updating workflow status for car ID:", carId);
      await apiService.updateCarWorkflowStatus({
        carId: carId,
        status: "PLACE_BIDS",
      });
      console.log(
        "Successfully updated workflow status to PLACE_BIDS for car ID:",
        carId
      );
      setIsUploadModalOpen(false);
      setSelectedCarId(null);
      // Update counts immediately
      setCars((prev) => {
        const newCars = prev.filter((car) => car.id !== carId);
        setUploadPicturesCount((prevCount) => prevCount - 1); // Decrement Upload Pictures count
        setPlaceBidsCount((prevCount) => prevCount + 1); // Increment Place Bids count
        return newCars;
      });
      setCachedCars((prev) =>
        prev ? prev.filter((car) => car.id !== carId) : null
      );
    } catch (err) {
      console.error("Error updating car workflow status:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      setError("Failed to update car status. Please try again.");
    } finally {
      setActionLoading(false); // Stop loading
    }
  };

  // Debug apiService to check available methods
  useEffect(() => {
    console.log("apiService methods:", Object.keys(apiService));
    if (!apiService.getDealerEvaluationCars) {
      console.error("apiService.getDealerEvaluationCars is undefined");
    }
  }, []);

  useEffect(() => {
    const fetchEvaluationCars = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("Fetching evaluation cars from API...");
        if (!apiService.getDealerEvaluationCars) {
          throw new Error(
            "getDealerEvaluationCars is not defined in apiService"
          );
        }
        const response = await apiService.getDealerEvaluationCars();
        console.log(
          "getDealerEvaluationCars response:",
          JSON.stringify(response, null, 2)
        );

        if (response.status === 304) {
          console.log("Received 304 Not Modified, checking cached data...");
          if (cachedCars) {
            console.log("Using cached evaluation cars:", cachedCars);
            setCars(cachedCars);
            setUploadedPictures(
              cachedCars.reduce(
                (acc, car) => ({
                  ...acc,
                  [car.id]: car.state === "EVALUATION_COMPLETE",
                }),
                {}
              )
            );
            setUploadPicturesCount(cachedCars.length);
            if (cachedCars.length === 0) {
              setError("No cars available in cache.");
            }
            setLoading(false);
            return;
          } else {
            console.warn("No cached data available for 304 response.");
            setError("No cached data available. Please try again.");
            setLoading(false);
            return;
          }
        }

        const yardGroups = response.data?.yardGroups || [];
        if (!Array.isArray(yardGroups)) {
          console.warn("yardGroups is not an array:", yardGroups);
          setError("Invalid data format received from server.");
          setCars([]);
          setCachedCars([]);
          setUploadPicturesCount(0);
          setLoading(false);
          return;
        }

        const validCars = yardGroups
          .flatMap((yardGroup) => yardGroup.cars || [])
          .filter((car) => car && typeof car === "object")
          .map((car) => {
            const mappedCar = {
              id: car.id || `car-${Math.random().toString(36).substr(2, 9)}`,
              car_id: car.car_id || null,
              brand: car.make || "Unknown",
              model: car.model || "Unknown",
              rto: car.regno || "N/A",
              fuel: car.fueltype || "Unknown",
              transmission: car.transmission || "N/A",
              year: Number(car.year) || 0,
              km: Number(car.kmsrun) || 0,
              image:
                car.car_image_urls?.length > 0
                  ? car.car_image_urls[0]
                  : car.car_image || "https://via.placeholder.com/150",
              timeLeft: car.ta_end_date
                ? formatTimeLeft(car.ta_end_date)
                : "N/A",
              location: car.yard || car.city || "Unknown",
              rc:
                car.rcstatus?.toUpperCase() === "YES" ||
                car.rcstatus?.toUpperCase() === "Y" ||
                car.rcstatus?.toUpperCase() === "AVAILABLE",
              owner: formatOwnership(car.ownership),
              yard: car.yard || "Unknown",
              startDate: car.start_date || null,
              endDate: car.ta_end_date || null,
              state: car.state || "EVALUATION",
              evaluationImages: car.evaluationImages || [],
              car_image_urls: car.car_image_urls || [],
              flowEntityId: car.flowEntityId || "",
            };
            console.log("Mapped car:", mappedCar);
            return mappedCar;
          })
          .filter((car) => {
            const isValid =
              car.id &&
              car.brand !== "Unknown" &&
              car.model !== "Unknown" &&
              car.rto !== "N/A" &&
              car.fuel !== "Unknown" &&
              typeof car.year === "number" &&
              typeof car.km === "number" &&
              car.location &&
              car.yard !== "Unknown";
            if (!isValid) {
              console.warn("Car filtered out due to invalid fields:", car);
            }
            return isValid;
          });

        console.log("Processed valid cars:", validCars);
        setCars(validCars);
        setCachedCars(validCars);
        setUploadPicturesCount(validCars.length);
        setUploadedPictures(
          validCars.reduce(
            (acc, car) => ({
              ...acc,
              [car.id]: car.state === "EVALUATION_COMPLETE",
            }),
            {}
          )
        );

        if (validCars.length === 0) {
          setError(
            yardGroups.length > 0
              ? "No cars meet the required criteria for evaluation. Please check the data."
              : "No car scheduled for evaluation"
          );
          setUploadPicturesCount(0);
        }
      } catch (err) {
        console.error("Error fetching evaluation cars:", {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
        });
        let errorMessage = "Failed to load evaluation cars. Please try again.";
        if (
          err.message === "getDealerEvaluationCars is not defined in apiService"
        ) {
          errorMessage =
            "API service configuration error: getDealerEvaluationCars is missing.";
        } else if (err.response?.status === 401) {
          errorMessage = "Unauthorized. Redirecting to login.";
          navigate("/login");
        } else if (err.response?.status === 400) {
          errorMessage = `Invalid request: ${
            err.response.data?.error || "Please check your inputs."
          }`;
        } else if (err.response?.status === 500) {
          errorMessage = `Server error: ${
            err.response.data?.error || "Please try again later."
          }`;
        } else if (err.code === "ECONNABORTED") {
          errorMessage = "Request timed out. Please check your network.";
        }
        setError(errorMessage);
        setUploadPicturesCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluationCars();
  }, [navigate, setUploadPicturesCount]);

  const handleUploadPictures = (carId) => {
    console.log("Opening upload modal for car ID:", carId);
    setSelectedCarId(carId);
    setIsUploadModalOpen(true);
  };

  // Updated handleRemoveCar to trigger loading
  const handleRemoveCar = async (carId) => {
    setActionLoading(true); // Start loading
    console.log(`Removing car with ID: ${carId}`);
    try {
      await apiService.removeFromMyCars({ carId: carId });
      setCars((prev) => {
        const newCars = prev.filter((car) => car.id !== carId);
        console.log(
          "Updated cars after removal, count:",
          newCars.length,
          newCars
        );
        setUploadPicturesCount(newCars.length);
        return newCars;
      });
      setCachedCars((prev) =>
        prev ? prev.filter((car) => car.id !== carId) : null
      );
      setUploadedPictures((prev) => {
        const newUploadedPictures = { ...prev };
        delete newUploadedPictures[carId];
        return newUploadedPictures;
      });
      if (selectedCarId === carId) {
        setIsUploadModalOpen(false);
        setSelectedCarId(null);
      }
    } catch (err) {
      console.error("Error removing car:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      setError("Failed to remove car. Please try again.");
    } finally {
      setActionLoading(false); // Stop loading
    }
  };

  const handleSubmitPictures = (carId, evaluationImages) => {
    console.log(`Pictures updated for car ID: ${carId}`, evaluationImages);
    setUploadedPictures((prev) => ({
      ...prev,
      [carId]: evaluationImages.length > 0,
    }));
    // Update the cars state with the new evaluationImages
    setCars((prev) =>
      prev.map((car) =>
        car.id === carId
          ? {
              ...car,
              evaluationImages,
              state:
                evaluationImages.length > 0
                  ? "EVALUATION_COMPLETE"
                  : "EVALUATION",
            }
          : car
      )
    );
    setCachedCars((prev) =>
      prev
        ? prev.map((car) =>
            car.id === carId
              ? {
                  ...car,
                  evaluationImages,
                  state:
                    evaluationImages.length > 0
                      ? "EVALUATION_COMPLETE"
                      : "EVALUATION",
                }
              : car
          )
        : null
    );
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
    setSelectedCarId(null);
  };

  console.log("Rendering UploadPictures, car count:", cars.length, cars);

  return (
    <ErrorBoundary>
      <div className="upload-pictures">
        <div className="content-wrapper">
          <div className="main-content">
            <div className="car-grid-container">
              {(loading || actionLoading) && <Loading />} {/* Updated: Show Loading for actionLoading */}
              {error && (
                <div className="no-results">
                  <h3>No cars found</h3>
                  <p>{error}</p>
                  <Button
                    className="see-more"
                    variant="primary"
                    onClick={() => navigate("/DealerHome")}
                  >
                    Back to Home
                  </Button>
                </div>
              )}
              {cars.length === 0 && !loading && !error && (
                <div className="no-results">
                  <h3>No cars found</h3>
                  <p>No car scheduled for evaluation</p>
                  <Button
                    className="btn-full"
                    variant="primary"
                    onClick={() => navigate("/DealerHome")}
                  >
                    Add Cars
                  </Button>
                </div>
              )}
              {cars.length > 0 && !loading && !error && (
                <div className="car-grid">
                  {cars.map((car, index) => (
                    <div
                      key={car.id}
                      className={`car-card ${car.isSold ? "sold" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/car-details/${car.id}`);
                      }}
                    >
                      {/* SOLD Overlay */}
                      {car.isSold && (
                        <div className="sold-overlay">
                          <div className="sold-badge">
                            <span className="sold-text">SOLD</span>
                          </div>
                        </div>
                      )}

                      <div className="car-image-wrapper">
                        <img src={imgTop} className="imgTop" alt="" />
                        <img src={imgBottom} className="imgBottom" alt="" />

                        {car.evaluationImages &&
                        car.evaluationImages.length > 0 ? (
                          <Swiper
                            modules={[Pagination, Autoplay]}
                            spaceBetween={0}
                            slidesPerView={1}
                            loop={true}
                            autoplay={{
                              delay: 2500,
                              disableOnInteraction: false,
                            }}
                            pagination={{ clickable: true }}
                            className="car-image-carousel"
                            allowTouchMove={true}
                          >
                            {car.evaluationImages.map((img, idx) => (
                              <SwiperSlide key={img.id || idx}>
                                <div className="carousel-image-container">
                                  <ImageWithFallback
                                    src={img.url}
                                    alt={`${car.brand} ${car.model} - ${
                                      img.angle || idx + 1
                                    }`}
                                    className="car-image"
                                  />
                                </div>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        ) : (
                          <ImageWithFallback
                            src={
                              car.car_image_urls &&
                              car.car_image_urls.length > 0
                                ? typeof car.car_image_urls[0] === "string"
                                  ? car.car_image_urls[0]
                                  : car.car_image_urls[0].url
                                : marutiImg
                            }
                            alt="Steering Wheel"
                            className="car-image"
                          />
                        )}

                        <span className="time-badge">{car.timeLeft}</span>
                        <div className="car-location">
                          <ImageWithFallback src={locCard} alt="Location" />
                          <p className="car-location-name">{car.location}</p>
                        </div>
                      </div>
                      <div className="car-details">
                        <div className="car-info">
                          <h3 className="car-title">
                            {car.year} {car.brand} {car.model}
                          </h3>
                          <div className="car-spec-container">
                            <div className="car-specs">
                              <span className="distance">
                                {car.rc ? (
                                  <img src={rc_yes} alt="" className="rc" />
                                ) : (
                                  <img src={rc_no} alt="" className="rc" />
                                )}
                                {car.km.toLocaleString()} kms
                              </span>
                              <span className="car-fuel">
                                {" • "}
                                {car.fuel} • {car.transmission}
                              </span>
                            </div>
                            <div className="car-tags">
                              {car.rc ? (
                                <img src={rc_yes} alt="RC Yes" className="rc" />
                              ) : (
                                <img src={rc_no} alt="RC No" className="rc" />
                              )}
                              <span className="rto-tag">{car.rto}</span>
                              <span className="owner-tag">{car.owner}</span>
                            </div>
                            <div className="phone-car-location">
                              <ImageWithFallback src={locCard} alt="Location" />
                              <p className="car-location-name">
                                {car.location}
                              </p>
                              <Button
                                className="btn-full phone-btn-full btn-primary"
                                variant="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUploadPictures(car.id);
                                }}
                              >
                                {car.state === "EVALUATION_COMPLETE"
                                  ? "View Pictures"
                                  : "Upload Pictures"}
                              </Button>
                            </div>
                          </div>
                          <img src={Delete} alt="" className="phoneBin" onClick={(e) => {
                                 e.stopPropagation();
                                handleRemoveCar(car.id);
                            }} />
                        </div>

                        <div className="car-actions">
                          <Button
                            className="btn-full"
                            variant="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUploadPictures(car.id);
                            }}
                          >
                            {car.state === "EVALUATION_COMPLETE"
                              ? "View Pictures"
                              : "Upload Pictures"}
                          </Button>
                          <div className="car-actions-footer">
                            <Button
                              className="btn-half btn-outline"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveCar(car.id);
                              }}
                              disabled={actionLoading} // Disable during action
                            >
                              <img src={Delete} alt="" />
                              Remove
                            </Button>
                            {car.state === "EVALUATION_COMPLETE" && car.evaluationImages.length !== 0 && (
                              <Button
                                className="btn-half btn-primary"
                                variant="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveToPlaceBid(car.id);
                                }}
                                disabled={actionLoading} // NEW: Disable during action
                              >
                                Move
                              </Button>
                            )}
                          </div>
                        </div>
                        
                      </div>
                      <Button
                        className="phone-btn-full"
                        variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUploadPictures(car.id);
                        }}
                      >
                        {car.state === "EVALUATION_COMPLETE"
                          ? "View Pictures"
                          : "Upload Pictures"}
                      </Button>
                      {car.state === "EVALUATION_COMPLETE" && car.evaluationImages.length !== 0 && (
                        <Button
                          className="phone-btn-full"
                          variant="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveToPlaceBid(car.id);
                          }}
                          disabled={actionLoading} // NEW: Disable during action
                        >
                          Move To Place Bids
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        car={
          cars.find((car) => car.id === selectedCarId) || {
            brand: "",
            model: "",
            year: "",
            rto: "",
            state: "EVALUATION",
            evaluationImages: [],
            car_image_urls: [],
          }
        }
        onSubmitPictures={handleSubmitPictures}
      />
    </ErrorBoundary>
  );
}